import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useVisitor } from '../context/VisitorContext';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSearch, FiFilter, FiDownload, FiEye, FiGlobe, FiFileText, FiShield, FiSlash, FiUserCheck } = FiIcons;

const Visitors = () => {
  const { visitors, isBlacklisted, addToBlacklist, user } = useVisitor();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMode, setFilterMode] = useState('all'); // 'all', 'companies', 'humans'

  // Helper to derive source
  const getSource = (v) => {
    const utmSource = v.utm_source || v.utmSource;
    const utmMedium = v.utm_medium || v.utmMedium;
    if (utmSource) return utmSource + (utmMedium ? ` / ${utmMedium}` : '');
    
    const referrer = v.referrer;
    if (referrer) {
      try {
        const url = new URL(referrer);
        const host = url.hostname.toLowerCase();
        if (host.includes('google')) return 'Google Search';
        if (host.includes('bing')) return 'Bing Search';
        if (host.includes('yahoo')) return 'Yahoo Search';
        if (host.includes('facebook')) return 'Facebook';
        if (host.includes('twitter') || host.includes('t.co')) return 'Twitter';
        if (host.includes('linkedin')) return 'LinkedIn';
        if (host.includes('chatgpt') || host.includes('openai')) return 'ChatGPT';
        return host;
      } catch(e) { return 'Referral'; }
    }
    return 'Direct';
  };

  const filteredVisitors = visitors.filter(visitor => {
    // 1. Search Filter
    const matchesSearch = !searchTerm || 
      (visitor.organization && visitor.organization.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (visitor.landingPage && visitor.landingPage.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (visitor.ip && visitor.ip.includes(searchTerm));

    // 2. Mode Filter
    let matchesMode = true;
    if (filterMode === 'companies') {
      matchesMode = !isBlacklisted(visitor.organization);
    } else if (filterMode === 'humans') {
      // Assuming 'visit_number' > 0 or explicit human flag if available. 
      // React context visitors might not have 'is_human' explicitly unless mapped.
      // We check if it's NOT a bot category from blacklist and maybe checks behavior if tracked.
      // For now, let's assume if it's NOT blacklisted as Bot it's human-ish, 
      // but ideally we rely on the backend 'is_human' flag if available.
      // Add fallback: simple check
      matchesMode = visitor.is_human === true || visitor.is_human === 1;
    }

    return matchesSearch && matchesMode;
  });

  const prepareExportData = (dataToExport) => {
    return dataToExport.map(v => ({
      'Organization': v.organization || 'Unknown',
      'Source': getSource(v),
      'Date/Time': format(new Date(v.timestamp), 'yyyy-MM-dd HH:mm:ss'),
      'Landing Page': v.landingPage || '/',
      'IP Address': v.ip,
      'Country': v.country_name || '',
      'City': v.city || '',
      'Is Human': v.is_human ? 'Yes' : 'No',
      'Duration (sec)': v.duration || 0,
      'Referrer URL': v.referrer || '',
      'UTM Campaign': v.utm_campaign || ''
    }));
  };

  const exportExcel = (mode) => {
    let dataToExport = visitors;
    
    if (mode === 'companies') {
      dataToExport = visitors.filter(v => !isBlacklisted(v.organization));
    } else if (mode === 'humans') {
      dataToExport = visitors.filter(v => v.is_human === true || v.is_human === 1);
    }

    const data = prepareExportData(dataToExport);
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    
    const sheetName = mode === 'companies' ? "Companies" : mode === 'humans' ? "Humans" : "All Visitors";
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    // Auto-width columns
    const max_width = data.length > 0 ? data.reduce((w, r) => Math.max(w, (r['Organization'] || '').length), 10) : 10;
    worksheet['!cols'] = [ { wch: max_width }, { wch: 20 }, { wch: 20 }, { wch: 30 }, { wch: 15 } ];

    const fileName = `Visitor_Report_${mode}_${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Visitors</h1>
        <p className="text-gray-600">Track and analyze your website visitors</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
          
          <div className="flex flex-col sm:flex-row gap-4 flex-1 w-full">
            <div className="relative flex-1 max-w-sm">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search visitors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
              />
            </div>
            
            {/* Filter Mode Toggle */}
            <div className="flex bg-gray-100 p-1 rounded-lg self-start sm:self-auto">
              <button 
                onClick={() => setFilterMode('all')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${filterMode === 'all' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                All
              </button>
              <button 
                onClick={() => setFilterMode('companies')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${filterMode === 'companies' ? 'bg-white text-green-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Companies
              </button>
              <button 
                onClick={() => setFilterMode('humans')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${filterMode === 'humans' ? 'bg-white text-purple-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Humans Only
              </button>
            </div>
          </div>

          <div className="flex gap-2">
            <button 
              onClick={() => exportExcel('humans')}
              className="flex items-center space-x-2 px-3 py-2 bg-purple-50 text-purple-700 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors"
            >
              <SafeIcon icon={FiUserCheck} />
              <span className="hidden sm:inline">Export Humans</span>
            </button>
            <button 
              onClick={() => exportExcel('companies')}
              className="flex items-center space-x-2 px-3 py-2 bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
            >
              <SafeIcon icon={FiShield} />
              <span className="hidden sm:inline">Export Companies</span>
            </button>
            <button 
              onClick={() => exportExcel('all')}
              className="flex items-center space-x-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <SafeIcon icon={FiDownload} />
            </button>
          </div>

        </div>
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-xl shadow-sm overflow-hidden">
        {filteredVisitors.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-4 px-6 font-medium text-gray-600">Organization</th>
                  <th className="text-left py-4 px-6 font-medium text-gray-600">Type</th>
                  <th className="text-left py-4 px-6 font-medium text-gray-600">Landing Page</th>
                  <th className="text-left py-4 px-6 font-medium text-gray-600">Date/Time</th>
                  <th className="text-left py-4 px-6 font-medium text-gray-600">Source</th>
                  <th className="text-right py-4 px-6 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredVisitors.map((visitor) => {
                  const isBlocked = isBlacklisted(visitor.organization);
                  const isHuman = visitor.is_human === true || visitor.is_human === 1;
                  return (
                    <tr key={visitor.id} className={`hover:bg-gray-50 transition-colors ${isBlocked ? 'opacity-60 bg-gray-50' : ''}`}>
                      <td className="py-4 px-6">
                        <div className="flex items-center space-x-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isBlocked ? 'bg-gray-200' : 'bg-blue-100'}`}>
                            <SafeIcon icon={isBlocked ? FiSlash : FiGlobe} className={`${isBlocked ? 'text-gray-500' : 'text-blue-600'} text-sm`} />
                          </div>
                          <div>
                            <span className={`font-medium ${isBlocked ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                              {visitor.organization || 'Unknown Organization'}
                            </span>
                            {isBlocked && <span className="ml-2 text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded">ISP/Bot</span>}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                         {isHuman ? (
                           <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                             <SafeIcon icon={FiUserCheck} className="mr-1" /> Human
                           </span>
                         ) : (
                           <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                             Bot / Unknown
                           </span>
                         )}
                      </td>
                      <td className="py-4 px-6 text-gray-600 text-sm truncate max-w-xs" title={visitor.landingPage}>{visitor.landingPage || '/'}</td>
                      <td className="py-4 px-6 text-gray-600 text-sm">
                        {format(new Date(visitor.timestamp), 'MMM dd, HH:mm')}
                      </td>
                      <td className="py-4 px-6">
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">
                          {getSource(visitor)}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-right">
                        {!isBlocked && user && visitor.organization && visitor.organization !== 'Unknown' && (
                          <button 
                            onClick={() => addToBlacklist(visitor.organization, 'ISP')}
                            className="text-gray-400 hover:text-red-600 text-xs flex items-center justify-end ml-auto"
                            title="Block this organization (Mark as ISP)"
                          >
                            <SafeIcon icon={FiSlash} className="mr-1" /> Block
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <SafeIcon icon={FiEye} className="text-4xl text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No visitors found</h3>
            <p className="text-gray-600">
              {filterMode !== 'all' ? `No ${filterMode} found. Try changing filters.` : 'Install the tracking script to start.'}
            </p>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default Visitors;